import { Fragment, useCallback, useEffect, useState } from "react";
import { ChevronDown, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, PageHeader } from "@/components/common/ui-bits";
import { api } from "@/lib/api";

const when = new Intl.DateTimeFormat("en-US", { timeZone: "America/Nassau", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const TYPES = [["transaction", "Transaction"], ["bill", "Bill"], ["payment", "Bill payment"], ["budget", "Budget"], ["category", "Category"],
  ["user", "User"], ["session", "Sign-in / sign-out"], ["import", "Import"], ["scan", "Scan"], ["data", "Clear data"]];
const ACTIONS = [["added", "Added"], ["edited", "Edited"], ["deleted", "Deleted"]];
const TONE = { added: "bg-emerald-500", edited: "bg-sky-500", deleted: "bg-rose-500" };

const Details = ({ d }) => (
  <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs rounded-lg bg-secondary/50 p-2.5">
    {Object.entries(d).map(([k, v]) => <Fragment key={k}><dt className="text-muted-foreground">{k}</dt><dd className="break-words">{String(v ?? "—")}</dd></Fragment>)}
  </dl>
);

function Entry({ a }) {
  const [open, setOpen] = useState(false);
  const more = a.items.length > 0 || Object.keys(a.details || {}).length > 0;
  return (
    <li className="px-4 py-3.5" data-testid={`activity-entry-${a.id}`}>
      <div className="flex gap-3">
        <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${TONE[a.action]}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm break-words" data-testid={`activity-text-${a.id}`}><b>{a.actor_name}</b> {a.summary}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{when.format(new Date(a.at))}</div>
          {more && (
            <button onClick={() => setOpen(!open)} className="mt-1 text-xs font-semibold text-sky-700 inline-flex items-center gap-1" data-testid={`activity-expand-${a.id}`}>
              {a.items.length ? `Show ${a.items.length} items` : "Show details"}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
          {open && Object.keys(a.details || {}).length > 0 && <Details d={a.details} />}
          {open && a.items.length > 0 && (
            <ul className="mt-2 space-y-2" data-testid={`activity-items-${a.id}`}>
              {a.items.map((it, i) => <li key={i} className="text-xs"><div className="font-semibold">{it.summary}</div>{it.details && <Details d={it.details} />}</li>)}
            </ul>
          )}
        </div>
      </div>
    </li>
  );
}

const Pick = ({ value, onChange, all, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11 rounded-xl bg-card" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="all">{all}</SelectItem>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function ActivityPage() {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ user_id: "all", action: "all", item_type: "all", start: "", end: "" });
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const set = (k) => (v) => { setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v })); setPage(1); };
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  const load = useCallback(async () => {
    const params = { page, page_size: 50 };
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    const { data: d } = await api.get("/activity", { params });
    setData((prev) => (page > 1 && prev ? { ...d, items: [...prev.items, ...d.items] } : d));
  }, [f, page]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Who changed what" title="Activity" subtitle="Every add, edit and delete. Read-only." testId="activity-title" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4" data-testid="activity-filters">
        <Pick value={f.user_id} onChange={set("user_id")} all="All users" options={users.map((u) => [u.id, u.name])} testId="activity-filter-user" />
        <Pick value={f.action} onChange={set("action")} all="All actions" options={ACTIONS} testId="activity-filter-action" />
        <Pick value={f.item_type} onChange={set("item_type")} all="All items" options={TYPES} testId="activity-filter-type" />
        <Input type="date" value={f.start} onChange={set("start")} className="h-11 rounded-xl" aria-label="From" data-testid="activity-filter-start" />
        <Input type="date" value={f.end} onChange={set("end")} className="h-11 rounded-xl" aria-label="To" data-testid="activity-filter-end" />
      </div>
      <ul className="surface divide-y divide-border overflow-hidden" data-testid="activity-list">
        {data?.items.length === 0 && <EmptyState icon={History} text="No activity found" testId="activity-empty" />}
        {data?.items.map((a) => <Entry key={a.id} a={a} />)}
      </ul>
      {data && data.items.length < data.total_items && (
        <Button variant="outline" className="mt-4 rounded-xl w-full" onClick={() => setPage((p) => p + 1)} data-testid="activity-load-more">Load more</Button>
      )}
    </div>
  );
}
